import { Component } from '@theme/component';
import { CartItemsComponent } from '@theme/component-cart-items';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping'
  ];

  #activeFetch = null;
  #pending = false;
  #needsRefresh = false;

  #disableControls() {
    const previousBusy = this.getAttribute('aria-busy');
    const input = this.querySelector('input[name="discount"]');
    const previousReadOnly = input instanceof HTMLInputElement && input.readOnly;

    const buttons = Array.from(this.querySelectorAll('button'), button => ({
      button,
      previousDisabled: button.getAttribute('aria-disabled'),
    }));

    this.setAttribute('aria-busy', 'true');

    if (input instanceof HTMLInputElement) input.readOnly = true;

    for (const { button } of buttons) {
      button.setAttribute('aria-disabled', 'true');
    }

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (input instanceof HTMLInputElement) {
        input.readOnly = previousReadOnly;
      }

      for (const { button, previousDisabled } of buttons) {
        if (previousDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previousDisabled);
      }
    };
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();

    const controller = new AbortController();
    this.#activeFetch = controller;

    return controller;
  }

  async #refreshDiscountSection(shouldRender) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#needsRefresh = true;
    let validated = false;

    try {
      while (!validated && this.isConnected) {
        let pending = CartItemsComponent.pendingQuantityUpdates;

        while (pending.length) {
          await Promise.all(pending);
          pending = CartItemsComponent.pendingQuantityUpdates;
        }

        if (!this.isConnected) return;

        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          shouldRender: html => {
            validated = true;
            return shouldRender?.(html) ?? true;
          },
        });
      }

      if (validated) this.#needsRefresh = false;

    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Failed to refresh cart discounts',
        code: 'SERVICE_UNAVAILABLE',
      }));
    }
  }

  async #refreshStaleDiscounts() {
    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      await this.#refreshDiscountSection();
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  /**
   * Actualiza exclusivamente el resumen y los descuentos.
   * No reemplaza los botones de pago ni el resto de la sección.
   */
  #updateCartVisuals(html) {
    const sectionId = this.dataset.sectionId;

    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const newSection = parsed.getElementById(
      `shopify-section-${sectionId}`
    );

    const currentSection = document.getElementById(
      `shopify-section-${sectionId}`
    );

    if (!newSection || !currentSection) return;

    // Actualizar el precio TOTAL.
    const newTotal = newSection.querySelector('.beldi-cart-total-row');
    const currentTotal = currentSection.querySelector('.beldi-cart-total-row');

    if (newTotal && currentTotal) {
      currentTotal.replaceWith(newTotal);
    }

    // Actualizar los descuentos aplicados.
    const newDiscounts = newSection.querySelector('.cart-discount__applied');
    const currentDiscounts = currentSection.querySelector('.cart-discount__applied');

    if (newDiscounts && currentDiscounts) {
      currentDiscounts.replaceWith(newDiscounts);
    } else if (newDiscounts && !currentDiscounts) {
      const content = currentSection.querySelector('.cart-discount__content');
      if (content) content.appendChild(newDiscounts);
    } else if (!newDiscounts && currentDiscounts) {
      currentDiscounts.remove();
    }

    // Actualizar el subtotal y las líneas de descuentos si existen.
    const newOriginal = newSection.querySelector('.cart-totals__original-container');
    const currentOriginal = currentSection.querySelector('.cart-totals__original-container');

    if (newOriginal && currentOriginal) {
      currentOriginal.replaceWith(newOriginal);
    } else if (newOriginal && !currentOriginal) {
      const totalRow = currentSection.querySelector('.beldi-cart-total-row');
      if (totalRow) totalRow.before(newOriginal);
    } else if (!newOriginal && currentOriginal) {
      currentOriginal.remove();
    }
  }

  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping
    } = this.refs;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');

    if (
      !(discountCode instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) return;

    const discountCodeValue = discountCode.value.trim();

    if (!discountCodeValue) return;

    const existingDiscounts = this.#existingDiscounts();

    if (existingDiscounts.includes(discountCodeValue)) return;

    sectionRenderer.abortRender(this.dataset.sectionId);

    const controller = this.#createAbortController();

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];

    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      this.dispatchEvent(new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map(code => ({ code })),
        promise: deferredPromise.promise,
      }));

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });

      const data = await response.json();

      const invalid = data.discount_codes?.find(discount =>
        discount.code === discountCodeValue &&
        discount.applicable === false
      );

      if (invalid) {
        discountCode.value = '';

        this.#handleDiscountError('discount_code');

        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });

        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];

      const shouldRender = html => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        const section = parsed.getElementById(
          `shopify-section-${this.dataset.sectionId}`
        );

        const pills = section?.querySelectorAll('.cart-discount__pill') || [];

        const codes = Array.from(pills)
          .map(element => element.dataset.discountCode)
          .filter(Boolean);

        if (
          section &&
          codes.length === existingDiscounts.length &&
          codes.every(code => existingDiscounts.includes(code)) &&
          data.discount_codes?.some(discount =>
            discount.code === discountCodeValue &&
            discount.applicable === true
          )
        ) {
          this.#handleDiscountError('shipping');
          return false;
        }

        return true;
      };

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (!newHtml) {
        discountCode.value = '';
        await this.#refreshDiscountSection(shouldRender);
        return;
      }

      if (!shouldRender(newHtml)) {
        discountCode.value = '';
        return;
      }

      // Limpiar el campo del cupón.
      discountCode.value = '';

      // IMPORTANTE:
      // No utilizar morphSection aquí.
      // Solo actualizamos el precio y los descuentos.
      // Los botones de pago permanecen intactos.
      this.#updateCartVisuals(newHtml);

    } catch (error) {
      deferredPromise.reject(error);

      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(new CartErrorEvent({
          error: error.message || 'Failed to apply discount',
          code: 'SERVICE_UNAVAILABLE',
        }));
      }

    } finally {
      this.#activeFetch = null;
      this.#pending = false;
      restoreControls();

      cartPerformance.measureFromEvent(
        'discount-update:user-action',
        event
      );
    }
  };

  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#pending) return;

    if (this.#needsRefresh) {
      await this.#refreshStaleDiscounts();
      return;
    }

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) return;

    const pill = event.target.closest('.cart-discount__pill');

    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);

    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    sectionRenderer.abortRender(this.dataset.sectionId);

    const controller = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.#pending = true;
    const restoreControls = this.#disableControls();

    try {
      this.dispatchEvent(new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map(code => ({ code })),
        promise: deferredPromise.promise,
      }));

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: controller.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      const newHtml = data.sections?.[this.dataset.sectionId];

      if (!newHtml) {
        await this.#refreshDiscountSection();
        return;
      }

      this.#updateCartVisuals(newHtml);

    } catch (error) {
      deferredPromise.reject(error);

      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(new CartErrorEvent({
          error: error.message || 'Failed to remove discount',
          code: 'SERVICE_UNAVAILABLE',
        }));
      }

    } finally {
      this.#activeFetch = null;
      this.#pending = false;
      restoreControls();
    }
  };

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping
    } = this.refs;

    const target = type === 'discount_code'
      ? cartDiscountErrorDiscountCode
      : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code'
      ? 'Invalid discount code'
      : 'Discount not applicable for shipping';

    this.dispatchEvent(new CartErrorEvent({
      error: errorMessage,
      code: 'VALIDATION_CUSTOM',
    }));
  }

  #existingDiscounts() {
    const discountCodes = [];

    const pills = this.querySelectorAll('.cart-discount__pill');

    for (const pill of pills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === 'string'
      ) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
